/**
 * The app's Light / Dark / System choice, carried to the user guide at `/help` (B-66).
 *
 * `/help` is static pages under a CSP with no script (`server/help.ts`), so it cannot read the
 * app's `localStorage`. Instead the app writes its choice to a small cookie (path `/help`, so it
 * goes nowhere else), and the server writes `data-theme` on the page's `<html>` from it; the
 * guide stylesheet honours that over `prefers-color-scheme`. "System" (or no cookie) leaves the
 * attribute off, and the guide follows the device as before. The value is a preference, never an
 * identifier, and only the two exact words below are ever acted on.
 */

export const HELP_THEME_COOKIE = "skydata-theme";
export const HELP_THEME_COOKIE_PATH = "/help";
const ONE_YEAR_SECONDS = 365 * 24 * 60 * 60;

export type HelpTheme = "light" | "dark";

/** The `Set-Cookie` text for `document.cookie`, from the app's stored preference. */
export function helpThemeCookie(preference: "light" | "dark" | "system", secure: boolean): string {
  return [
    `${HELP_THEME_COOKIE}=${preference}`,
    `path=${HELP_THEME_COOKIE_PATH}`,
    `max-age=${ONE_YEAR_SECONDS}`,
    "SameSite=Lax",
    ...(secure ? ["Secure"] : []),
  ].join("; ");
}

/** The theme a request's `Cookie` header asks for; null for System, absent or anything else. */
export function helpThemeFromCookie(header: string | undefined): HelpTheme | null {
  if (!header) return null;
  for (const part of header.split(";")) {
    const [name, value] = part.trim().split("=");
    if (name === HELP_THEME_COOKIE) {
      return value === "light" || value === "dark" ? value : null;
    }
  }
  return null;
}

/** The page with `data-theme` on its `<html>` element; unchanged for null. */
export function applyHelpTheme(html: string, theme: HelpTheme | null): string {
  if (theme === null) return html;
  return html.replace(/<html(?![^>]*data-theme)/, `<html data-theme="${theme}"`);
}
